import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MaterialRequestActual from './components/MaterialPrice/MaterialActual';
import MaterialRequestMax from './components/MaterialPrice/MaterialMax';
import MaterialRequest from './components/MaterialPrice/Materila';
import './styles/MaterialPage.css';
import Dynamics from './components/MaterialPrice/Dynamics';
import DynamicsPe from './components/MaterialPrice/DynamicsPe';
import { getPdfButtonLabel, useRegistryPdfExport } from './hooks/useRegistryPdfExport';
import {
  MaterialActualPdfData,
  MaterialHistoryPdfData,
  MaterialMaxPdfData,
  buildMaterialsDocument,
  captureChartCanvas,
} from './utils/materialsPdfUtils';

const MaterialPage: React.FC = () => {
  const [materialType, setMaterialType] = useState<'pe' | 'ppr' | 'pvh'>('pe');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const contentRef = useRef<HTMLDivElement>(null);
  const { pdfProgress, startPdfExport, pdfReport } = useRegistryPdfExport();

  useEffect(() => {
    const typeParam = (searchParams.get('type') || '').toLowerCase();
    if (typeParam === 'pe' || typeParam === 'ppr' || typeParam === 'pvh') {
      setMaterialType(typeParam as 'pe' | 'ppr' | 'pvh');
    }
  }, [searchParams]);

  const handleMaterialTypeChange = (type: string) => {
    setMaterialType(type as 'pe' | 'ppr' | 'pvh');
    setSearchParams({ type });
  };

  // Блоки страницы сами грузят данные и присылают сюда то, что показывают, из этого собирается PDF
  const actualPricesRef = useRef<MaterialActualPdfData | null>(null);
  const maxPricesRef = useRef<MaterialMaxPdfData | null>(null);
  const priceHistoryRef = useRef<MaterialHistoryPdfData | null>(null);
  const handleActualPricesChange = useCallback((actualPrices: MaterialActualPdfData) => { actualPricesRef.current = actualPrices; }, []);
  const handleMaxPricesChange = useCallback((maxPrices: MaterialMaxPdfData) => { maxPricesRef.current = maxPrices; }, []);
  const handlePriceHistoryChange = useCallback((priceHistory: MaterialHistoryPdfData) => { priceHistoryRef.current = priceHistory; }, []);

  const handleDownload = () => startPdfExport(() => {
    // Графики берём с canvas Chart.js в том виде, как они сейчас на странице
    const chartCanvases: HTMLCanvasElement[] = [];
    if (contentRef.current) {
      contentRef.current.querySelectorAll('.line-chart-container canvas').forEach(canvas => {
        if ((canvas as HTMLElement).offsetParent !== null) chartCanvases.push(canvas as HTMLCanvasElement);
      });
    }
    const charts = chartCanvases.map((canvas, index) => ({
      title: chartCanvases.length > 1 ? `Динамика цен (${index + 1} из ${chartCanvases.length})` : 'Динамика цен',
      image: captureChartCanvas(canvas),
    }));
    return buildMaterialsDocument(materialType, actualPricesRef.current, maxPricesRef.current, priceHistoryRef.current, charts);
  });

  return (
    <>
      <div className="navigation_material">
        <nav>
          <span
            className="breadcrumb-home"
            style={{ color: 'rgba(0,0,0,0.5)', cursor: 'pointer', fontWeight: 400 }}
            onClick={() => navigate('/')}
          >
            Главная страница
          </span>
          <span style={{ margin: '0 8px', color: 'rgba(0,0,0,0.3)' }}>/</span>
          <span className="breadcrumb-current" style={{ fontWeight: 500, color: '#000' }}>
          Цены на полимерное сырье
          </span>
        </nav>
        <button className="download-button" onClick={handleDownload} disabled={pdfProgress !== null}>
          {getPdfButtonLabel(pdfProgress)}
        </button>
      </div>
      {pdfReport}
      <div ref={contentRef} className="material-page-container">
        <div className="material-page-header">
          <h1 className="material-page-title">Цены на полимерное сырье</h1>
          <div className="material-type-tabs">
            <button
              className={`material-type-tab ${materialType === 'pe' ? 'active' : ''}`}
              onClick={() => handleMaterialTypeChange('pe')}
            >
              Полиэтилен
            </button>
            <button
              className={`material-type-tab ${materialType === 'ppr' ? 'active' : ''}`}
              onClick={() => handleMaterialTypeChange('ppr')}
            >
              Полипропилен
            </button>
            <button
              className={`material-type-tab ${materialType === 'pvh' ? 'active' : ''}`}
              onClick={() => handleMaterialTypeChange('pvh')}
            >
              Поливинилхлорид
            </button>
          </div>
        </div>
        <div className="material-content-area">
          <MaterialRequestActual materialType={materialType} onPdfDataChange={handleActualPricesChange} />
          <MaterialRequestMax materialType={materialType} onPdfDataChange={handleMaxPricesChange} />
          {materialType === 'pe' ? <DynamicsPe /> : <Dynamics materialType={materialType} />}
          <MaterialRequest materialType={materialType} onPdfDataChange={handlePriceHistoryChange} />
        </div>
      </div>
    </>
  );
};

export default MaterialPage;